/**
 * @module ServiceCheckForm
 * @description
 */
import React, { FC, MouseEvent, FormEvent } from 'react';
import { useState, useRef } from 'react';
import debounce from 'lodash/debounce';
import { usePredictiveSearch } from 'ui/hooks/usePredictiveSearch';
import { isValidAddressInput } from 'ui/js/utils/validators';
import { stringify } from 'ui/js/utils/filters';
import { trackEvent } from 'ui/js/helpers/tracking';
import { Address } from '~/types/address';
import { v4 as uuidv4 } from 'uuid';
import styles from './styles/ServiceCheckSingleLine.module.css';

const formContext = {
  formType: 'SERVICE CHECK - SINGLE LINE',
  formName: 'SERVICE CHECK - SINGLE LINE'
};

interface Props {
  isDark?: boolean;
  isHero?: boolean;
}

const ServiceCheckSingleLine: FC<Props> = ( { ...props } ) => {
  const {
    isDark,
    isHero
  } = props;

  const [ addressValid, setAddressValid ] = useState( false );
  const [ addressError, setAddressError ] = useState( false );
  const [ suggestions, setSuggestions ] = useState( [ ] );
  const [ addressObject, setAddressObject ] = useState( { } );
  const [ formStarted, setFormStarted ] = useState( false );
  const [ addressKey, setAddressKey ] = useState( false );
  const id = uuidv4();

  const addressField = useRef( null );
  const honeyPotField = useRef( null );

  const predictive = usePredictiveSearch( { addressField, honeyPotField }, setSuggestions, formContext );

  const setSuggestionValue = async ( event:MouseEvent ) => {
    const { address, addressData } = await predictive.setSuggestionValue( event );

    setAddressValid( isValidAddressInput( address ) );
    setAddressError( ! isValidAddressInput( address ) );
    setAddressObject( addressData );
    setAddressKey( true );
  };

  const fetchAddress = async ( event: FormEvent ) => {
    if ( ! formStarted ) {
      trackEvent( { action: 'formStarted', data: formContext } );
      setFormStarted( true );
    }

    predictive.fetchAddress( event );
  };

  return (
    <div className={ `${ styles.component } ${ isDark ? styles.isDark : '' } ${ isHero ? styles.isHero : '' }` }>
      <form name="service-check-form" className={ styles.form } autoComplete="off" onSubmit={ predictive.handleSubmit } data-address-object={ stringify( addressObject ) } data-cypress="service-check-form">
        <span className={ `${ styles.icon } icon icon-location-marker-2` } />
        <div className={ `field ${ styles.formField }` }>
          <label className="screen-reader-text" htmlFor={ `street-address-${ id }` }>Street Address</label>
          <input
            ref={ addressField }
            type="text"
            name="street-address"
            id={ `street-address-${ id }` }
            data-test-id="street-address"
            onInput={ debounce( fetchAddress, 500 ) }
            className="input fs-mask"
            placeholder="Enter your address."
            data-payload={ stringify( {
              fieldName: 'street',
              fieldType: 'street',
              fieldValue: 'STREET ADDRESS'
            } ) }/>
          <span className={ `field-error ${ addressError === true ? '--active' : '' }` }>Invalid Address</span>
          <div className={ `${ styles.suggestions } ${ suggestions && suggestions.length > 0 ? styles.suggestionsActive : '' }` }>
            { suggestions && suggestions.length > 0 &&
              <>
                <ul className={ styles.suggestionsList }>
                  { suggestions.map( ( suggestion: Address, index ) => (
                      <li key={ index }>
                        <button
                          type="button"
                          role="button"
                          data-address={ suggestion.value }
                          data-zip={ suggestion.zipCode }
                          data-object={ stringify( suggestion ) }
                          onClick={ setSuggestionValue }
                        >
                          { suggestion.value }
                        </button>
                      </li>
                  ) ) }
                </ul>
                <div className={ styles.manual }>
                  <button
                    type="button"
                    role="button"
                    onClick={ () => setSuggestions( [ ] ) }
                  >Close address suggestions</button>
                </div>
              </>
            }
          </div>{ /* <!-- .suggestions --> */ }
        </div>{ /* <!-- .field --> */ }

        <div className="field" data-hidden-input>
          <label htmlFor={ `hidden-input-${ id }` }>Hidden Input</label>
          <input
            ref={ honeyPotField }
            type="text"
            name="hidden-input"
            id={ `hidden-input-${ id }` }
            onInput={ predictive.handleInput }
            data-payload={ stringify( {
              fieldName: 'honeypot',
              fieldType: 'honeypot',
              fieldValue: 'HONEYPOT'
            } ) }
          />
        </div>{ /* <!-- .field --> */ }

        <button
          type="submit"
          className="btn"
          disabled={ addressValid !== true || addressKey !== true }
          aria-disabled={ addressValid !== true || addressKey !== true }
          role="button"
        >
          Go
        </button>
      </form>
    </div>
  );
};

ServiceCheckSingleLine.displayName = 'ServiceCheckSingleLine';

export default ServiceCheckSingleLine;
